import {action, computed, makeAutoObservable, observable, reaction} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {Bindings} from "data/constants/bindings";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import type {IConferencesStore} from "data/stores/conferences/conferences.store";
import type {IDivisionsStore} from "data/stores/divisions/divisions.store";
import type {ITournamentsStore} from "data/stores/tournaments/tournaments.store";
import type {ISquadsStore} from "data/stores/squads/squads.store";
import type {IStandingsStore, IUserStanding} from "data/stores/standings/standings.store";
import type {ChangeEvent, SyntheticEvent} from "react";
import type {IHeavyPredictionsStore} from "data/stores/heavy_predictions/heavy_predictions.store";
import {
	ConferenceType,
	HPGameTabs,
	IncreaseTarget,
	TournamentStatus,
	TutorialType,
} from "data/enums";
import {eq, findIndex, includes, isEqual, map, orderBy} from "lodash";
import type {ILightPredictionsStore} from "data/stores/light_predictions/light_predictions.store";
import type {IUserStore} from "data/stores/user/user.store";
import type {ITutorialStore} from "data/stores/tutorial/tutorial.store";
import type {IOrderedStandingsStore} from "data/stores/ordered_standings/ordered_standings.store";
import {ConnextraType, createConnextraScript} from "data/utils/createConnextraScript";
import {useNavigate} from "react-router-dom";
import {OFF_SEASON_PATH} from "data/constants";

interface IProps {
	navigate: ReturnType<typeof useNavigate>;
}
export interface IHeavyPredictorPageController extends ViewController<IProps> {
	readonly i18n: ILocalizationStore;
	isLoading: boolean;

	get isCurrentStandingsActive(): boolean;

	get activeTabValue(): HPGameTabs;

	get isAFCConferenceActive(): boolean;

	get nfcStandings(): IUserStanding[];

	get afcStandings(): IUserStanding[];

	get userNfcStandings(): IUserStanding[];

	get userAfcStandings(): IUserStanding[];

	get isOffSeason(): boolean;

	setCurrentStandingsActive: (event: ChangeEvent<HTMLInputElement>) => void;
	setActiveConference: (event: ChangeEvent<HTMLInputElement>) => void;
	changeTabs: (_event: SyntheticEvent, newValue: HPGameTabs) => void;
	openTutorialHandler: () => void;
}

@injectable()
export class HeavyPredictorPageController implements IHeavyPredictorPageController {
	@observable isLoading: boolean = false;
	@observable private _isCurrentStandingsActive: boolean = false;
	@observable private _activeTab: HPGameTabs = HPGameTabs.Schedule;
	@observable private _activeConference: ConferenceType = ConferenceType.NFC;
	@observable private _navigate?: ReturnType<typeof useNavigate>;
	private _disposerReaction!: ReturnType<typeof reaction>;

	get isCurrentStandingsActive() {
		return this._isCurrentStandingsActive;
	}

	get activeTabValue() {
		return this._activeTab;
	}

	get isAFCConferenceActive() {
		return this._activeConference === ConferenceType.AFC;
	}

	@computed get nfcStandings() {
		return this.createLiveStandingArr(
			this._standingsStore.standings,
			this._conferenceStore.nfcConferenceId
		);
	}

	@computed get afcStandings() {
		return this.createLiveStandingArr(
			this._standingsStore.standings,
			this._conferenceStore.afcConferenceId
		);
	}

	@computed get userNfcStandings() {
		return this.createUserStandingArr(
			this._standingsStore.userStandings,
			this._conferenceStore.nfcConferenceId
		);
	}

	@computed get userAfcStandings() {
		return this.createUserStandingArr(
			this._standingsStore.userStandings,
			this._conferenceStore.afcConferenceId
		);
	}

	get isOffSeason() {
		return this._roundStore.isOffSeason;
	}

	constructor(
		@inject(Bindings.LocalizationStore) readonly i18n: ILocalizationStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.RoundsStore) private _roundStore: IRoundsStore,
		@inject(Bindings.ConferencesStore) private _conferenceStore: IConferencesStore,
		@inject(Bindings.DivisionsStore) private _divisionStore: IDivisionsStore,
		@inject(Bindings.TournamentsStore) private _tournamentStore: ITournamentsStore,
		@inject(Bindings.SquadsStore) private _squadStore: ISquadsStore,
		@inject(Bindings.StandingsStore) private _standingsStore: IStandingsStore,
		@inject(Bindings.HeavyPredictionsStore) private _hpStore: IHeavyPredictionsStore,
		@inject(Bindings.LightPredictionsStore) private _lpStore: ILightPredictionsStore,
		@inject(Bindings.TutorialStore) private _tutorialStore: ITutorialStore,
		@inject(Bindings.OrderedStandingsStore) private _orderStandingsStore: IOrderedStandingsStore
	) {
		makeAutoObservable(this);
	}

	private setIsLoading(value: boolean): void {
		this.isLoading = value;
	}

	@computed private createLiveStandingArr = (
		standings: IUserStanding[],
		conferenceId: number
	) => {
		const actualStandingsMatches = this._tournamentStore.matchesByRoundId(
			this._roundStore.actualStandingsRoundId
		);
		const hasCompleteMatch = actualStandingsMatches.find(
			(match) => match.status === TournamentStatus.Complete
		);

		const standingsRoundId = hasCompleteMatch
			? this._roundStore.actualStandingsRoundId
			: this._roundStore.lastCompleteRoundId;

		const standingByRound = standings.filter((it) => {
			if (it.roundId === standingsRoundId) {
				const squad = this._squadStore.getSquadById(it.squadId);
				return eq(squad?.conferenceId, conferenceId);
			}
			return false;
		});

		// const result = orderBy(standingByRound, ["conferenceRank"], "asc");

		// console.log(conferenceId, "---", JSON.parse(JSON.stringify(result)));

		return orderBy(standingByRound, ["conferenceRank"], "asc");
	};

	@computed private createUserStandingArr = (
		standings: IUserStanding[],
		conferenceId: number
	) => {
		const actualStandingsMatches = this._tournamentStore.matchesByRoundId(
			this._roundStore.actualStandingsRoundId
		);
		const hasCompleteMatch = actualStandingsMatches.find(
			(match) => match.status === TournamentStatus.Complete
		);

		const standingsRoundId = hasCompleteMatch
			? this._roundStore.actualStandingsRoundId
			: this._roundStore.lastCompleteRoundId;

		const defaultStandings = this._standingsStore.standings.filter((it) => {
			if (it.roundId === standingsRoundId) {
				const squad = this._squadStore.getSquadById(it.squadId);
				return eq(squad?.conferenceId, conferenceId);
			}
			return false;
		});

		const standingByRound = standings.filter((it) => {
			if (it.roundId === standingsRoundId) {
				const squad = this._squadStore.getSquadById(it.squadId);
				return eq(squad?.conferenceId, conferenceId);
			}
			return false;
		});

		const orderedDefaultStandings = this._orderStandingsStore.getSortedStandings(
			defaultStandings,
			conferenceId
		);
		const orderedStandingsWithPicks = this._orderStandingsStore.getSortedStandings(
			standingByRound,
			conferenceId
		);

		return orderedStandingsWithPicks.map((el, index) => {
			const squadIndexInDefaultList = findIndex(
				orderedDefaultStandings,
				(it) => it.squadId === el.squadId
			);

			return {
				...el,
				position: squadIndexInDefaultList + 1,
				newPosition: index + 1,
			};
		});
	};

	openTutorialHandler = () => {
		this._tutorialStore.openTutorial(TutorialType.HEAVE_PREDICTOR);
	};

	@action setCurrentStandingsActive = (event: ChangeEvent<HTMLInputElement>) => {
		this._isCurrentStandingsActive = event.target.checked;
	};

	@action changeTabs = (_event: SyntheticEvent, newValue: HPGameTabs) => {
		this._activeTab = newValue;
	};

	@action setActiveConference = (event: ChangeEvent<HTMLInputElement>) => {
		this._activeConference = event.target.checked ? ConferenceType.AFC : ConferenceType.NFC;
	};

	private fillUserList() {
		this._standingsStore.clearUserList();

		map(this._hpStore.predictions, (predictions, roundId) => {
			map(predictions, (prediction) => {
				const match = this._tournamentStore.tournaments.find(
					(it) =>
						it.roundId === Number(roundId) &&
						[it.homeSquadId, it.awaySquadId].includes(prediction.squadId)
				);

				if (
					!match ||
					includes([TournamentStatus.Complete, TournamentStatus.Canceled], match.status)
				)
					return;

				const loseSquadId = isEqual(prediction.squadId, match.homeSquadId)
					? match.awaySquadId
					: match.homeSquadId;

				// common
				this._standingsStore.increaseWinsOrLosses(
					prediction.squadId,
					this._roundStore.actualStandingsRoundId,
					IncreaseTarget.Wins
				);
				this._standingsStore.increaseWinsOrLosses(
					loseSquadId,
					this._roundStore.actualStandingsRoundId,
					IncreaseTarget.Losses
				);

				//division
				const squad = this._squadStore.getSquadById(prediction.squadId);
				const opponentSquad = this._squadStore.getSquadById(loseSquadId);

				if (squad?.divisionId === opponentSquad?.divisionId) {
					this._standingsStore.increaseDivisionWinsOrLosses(
						prediction.squadId,
						this._roundStore.actualStandingsRoundId,
						IncreaseTarget.Wins
					);
					this._standingsStore.increaseDivisionWinsOrLosses(
						loseSquadId,
						this._roundStore.actualStandingsRoundId,
						IncreaseTarget.Losses
					);
				}

				//conference
				if (squad?.conferenceId === opponentSquad?.conferenceId) {
					this._standingsStore.increaseConfWinsOrLosses(
						prediction.squadId,
						this._roundStore.actualStandingsRoundId,
						IncreaseTarget.Wins
					);
					this._standingsStore.increaseConfWinsOrLosses(
						loseSquadId,
						this._roundStore.actualStandingsRoundId,
						IncreaseTarget.Losses
					);
				}

				this._standingsStore.updatePercents(
					prediction.squadId,
					this._roundStore.actualStandingsRoundId
				);

				this._standingsStore.updatePercents(
					loseSquadId,
					this._roundStore.actualStandingsRoundId
				);
			});
		});

		const division = this._squadStore.divisions;
		map(division, (squads) => {
			const squadIds = squads.map((it) => it.id);
			this._standingsStore.updateDivisionRanks(
				squadIds,
				this._roundStore.actualStandingsRoundId
			);
		});
	}

	private showTutorialModal = () => {
		const HPTutorial = Boolean(JSON.parse(localStorage.getItem("HP_tutorial") || "false"));
		// if (!this._lpStore.isPredictionsSaved) {
		if (!HPTutorial && !this.isOffSeason) {
			this.openTutorialHandler();
		}
	};

	dispose() {
		this._disposerReaction?.();
	}

	async init(param: IProps) {
		this._navigate = param.navigate;

		this.setIsLoading(true);

		await Promise.all([
			this._roundStore.fetchRounds(),
			this._squadStore.fetchSquads(),
			this._conferenceStore.fetchConferences(),
			this._divisionStore.fetchDivisions(),
			this._tournamentStore.fetchTournaments(),
			this._standingsStore.fetchStandings(),
		]);

		if (this._userStore.isAuthorized) {
			await this._lpStore.getPredictions();
		}

		if (this._roundStore.actualRound?.id) {
			this._roundStore.setSelectedRoundId(this._roundStore.actualRound.id);
		}

		this._hpStore.setPredictionsFromLocalStorage();

		this._disposerReaction = reaction(
			() => this._hpStore.predictions,
			(_value) => {
				this.fillUserList();
			},
			{
				fireImmediately: true,
			}
		);

		if (this._roundStore.isOffSeason) {
			this._navigate?.(OFF_SEASON_PATH, {replace: true});
		}

		createConnextraScript(ConnextraType.HP_PAGE, this._userStore.user?.id);

		this.setIsLoading(false);

		this.showTutorialModal();
	}
}
